import React, { useState, useEffect } from 'react';
import { organizationsAPI, familyArchersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Building2,
  Plus,
  Trash2,
  Edit2,
  CheckCircle,
  Shield,
  AlertCircle,
  User,
  Users,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const ORGANIZATIONS = [
  { id: 'USAA', name: 'USA Archery', color: 'bg-red-100 text-red-700' },
  { id: 'NFAA', name: 'National Field Archery Association', color: 'bg-blue-100 text-blue-700' },
  { id: 'TSAA', name: 'Texas State Archery Association', color: 'bg-orange-100 text-orange-700' },
  { id: 'TFAA', name: 'Texas Field Archery Association', color: 'bg-green-100 text-green-700' },
  { id: 'ASA', name: 'Archery Shooters Association', color: 'bg-purple-100 text-purple-700' },
  { id: 'IBO', name: 'International Bowhunting Organization', color: 'bg-teal-100 text-teal-700' }
];

const BOW_TYPES = ['Recurve', 'Compound', 'Barebow', 'Longbow', 'Traditional'];
const AGE_CLASSES = ['Youth', 'Junior', 'Senior', 'Master'];

export default function Organizations() {
  const { user } = useAuth();
  const isParent = user?.role === 'parent';
  
  const [linkedOrgs, setLinkedOrgs] = useState([]);
  const [familyArchers, setFamilyArchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [filterArcherId, setFilterArcherId] = useState('ALL');
  
  const [formData, setFormData] = useState({
    org_name: '',
    member_id: '',
    archer_name: '',
    bow_type: '',
    age_class: '',
    family_archer_id: ''
  });

  useEffect(() => {
    fetchData();
  }, [isParent]);

  const fetchData = async () => {
    try {
      const [orgsRes, archersRes] = await Promise.all([
        organizationsAPI.getAll(),
        isParent ? familyArchersAPI.getAll() : Promise.resolve({ data: [] })
      ]);
      setLinkedOrgs(orgsRes.data);
      setFamilyArchers(archersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      org_name: '',
      member_id: '',
      archer_name: '',
      bow_type: '',
      age_class: '',
      family_archer_id: ''
    });
    setEditingOrg(null);
  };

  const handleSubmit = async () => {
    if (!formData.org_name || !formData.member_id) {
      toast.error('Organization and Member ID are required');
      return;
    }

    // For parents with archers, encourage archer selection
    if (isParent && familyArchers.length > 0 && !formData.family_archer_id) {
      const confirm = window.confirm(
        'No archer selected. This organization will be linked to your account, not a specific archer. Continue?'
      );
      if (!confirm) return;
    }

    try {
      const payload = {
        ...formData,
        family_archer_id: formData.family_archer_id || null
      };

      if (editingOrg) {
        await organizationsAPI.update(editingOrg.id, payload);
        toast.success('Organization updated');
      } else {
        await organizationsAPI.link(payload);
        toast.success('Organization linked successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save organization';
      toast.error(message);
    }
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setFormData({
      org_name: org.org_name,
      member_id: org.member_id,
      archer_name: org.archer_name || '',
      bow_type: org.bow_type || '',
      age_class: org.age_class || '',
      family_archer_id: org.family_archer_id || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (orgId) => {
    if (!window.confirm('Remove this organization link?')) return;

    try {
      await organizationsAPI.delete(orgId);
      setLinkedOrgs(linkedOrgs.filter((o) => o.id !== orgId));
      toast.success('Organization unlinked');
    } catch (error) {
      toast.error('Failed to remove organization');
    }
  };

  const getOrgInfo = (orgId) => {
    return ORGANIZATIONS.find((o) => o.id === orgId) || { name: orgId, color: 'bg-slate-100 text-slate-700' };
  };

  // Filter organizations by archer
  const filteredOrgs = filterArcherId === 'ALL'
    ? linkedOrgs
    : linkedOrgs.filter(org => 
        filterArcherId === 'UNASSIGNED' 
          ? !org.family_archer_id 
          : org.family_archer_id === filterArcherId
      );

  // For available orgs, consider archer-specific linking for parents
  const getAvailableOrgs = () => {
    if (!isParent || !formData.family_archer_id) {
      // Show orgs not linked to account level (no archer)
      return ORGANIZATIONS.filter(
        org => !linkedOrgs.some(linked => 
          linked.org_name === org.id && !linked.family_archer_id
        )
      );
    }
    // For parents with archer selected, show orgs not linked to that specific archer
    return ORGANIZATIONS.filter(
      org => !linkedOrgs.some(linked => 
        linked.org_name === org.id && linked.family_archer_id === formData.family_archer_id
      )
    );
  };

  const availableOrgs = getAvailableOrgs();

  // Auto-populate archer name when archer is selected
  const handleArcherChange = (archerId) => {
    const archer = familyArchers.find(a => a.id === archerId);
    setFormData({
      ...formData,
      family_archer_id: archerId,
      archer_name: archer?.name || formData.archer_name,
      bow_type: archer?.bow_type || formData.bow_type,
      age_class: archer?.age_class || formData.age_class
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600">Loading organizations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="organizations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">My Organizations</h1>
          <p className="text-slate-600 mt-1">
            Link your archery organization accounts to track tournaments and results
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Archer filter for parents */}
          {isParent && familyArchers.length > 0 && (
            <Select value={filterArcherId} onValueChange={setFilterArcherId}>
              <SelectTrigger className="w-44" data-testid="org-archer-filter">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Filter by archer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Organizations</SelectItem>
                <SelectItem value="UNASSIGNED">Account Level</SelectItem>
                {familyArchers.map((archer) => (
                  <SelectItem key={archer.id} value={archer.id}>
                    {archer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-org-btn">
                <Plus className="w-4 h-4 mr-2" />
                Link Organization
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingOrg ? 'Edit Organization' : 'Link Organization'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Archer selector for parents */}
                {isParent && familyArchers.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Link for Archer
                    </Label>
                    <Select
                      value={formData.family_archer_id || 'NONE'}
                      onValueChange={(value) => handleArcherChange(value === 'NONE' ? '' : value)}
                    >
                      <SelectTrigger data-testid="org-archer-select">
                        <SelectValue placeholder="Select an archer (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">Account Level (No specific archer)</SelectItem>
                        {familyArchers.map((archer) => (
                          <SelectItem key={archer.id} value={archer.id}>
                            {archer.name} {archer.age_class && `(${archer.age_class})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Select which archer this membership belongs to, or leave blank for account-level linking.
                    </p>
                  </div>
                )}

                {isParent && familyArchers.length === 0 && (
                  <Alert className="border-blue-200 bg-blue-50">
                    <Users className="w-4 h-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      <strong>Tip:</strong> Add your archers in the{' '}
                      <Link to="/family-archers" className="underline text-blue-900">Family Archers</Link>{' '}
                      page to link organizations per archer.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label>Organization *</Label>
                  <Select
                    value={formData.org_name}
                    onValueChange={(value) => setFormData({ ...formData, org_name: value })}
                    disabled={!!editingOrg}
                  >
                    <SelectTrigger data-testid="org-select">
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {(editingOrg ? ORGANIZATIONS : availableOrgs).map((org) => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Member ID *</Label>
                  <Input
                    placeholder="Enter your member ID"
                    value={formData.member_id}
                    onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
                    data-testid="member-id-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Archer Name (as registered)</Label>
                  <Input
                    placeholder="Name as registered with organization"
                    value={formData.archer_name}
                    onChange={(e) => setFormData({ ...formData, archer_name: e.target.value })}
                    data-testid="archer-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Bow Type</Label>
                    <Select
                      value={formData.bow_type}
                      onValueChange={(value) => setFormData({ ...formData, bow_type: value })}
                    >
                      <SelectTrigger data-testid="bow-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {BOW_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Age Class</Label>
                    <Select
                      value={formData.age_class}
                      onValueChange={(value) => setFormData({ ...formData, age_class: value })}
                    >
                      <SelectTrigger data-testid="age-class-select">
                        <SelectValue placeholder="Select class" />
                      </SelectTrigger>
                      <SelectContent>
                        {AGE_CLASSES.map((cls) => (
                          <SelectItem key={cls} value={cls}>
                            {cls}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button className="btn-primary" onClick={handleSubmit} data-testid="save-org-btn">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {editingOrg ? 'Update' : 'Link Organization'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Shield className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-blue-800 font-medium">
                {isParent 
                  ? 'Link organization memberships for each of your family archers'
                  : 'Link your member accounts to automatically track tournaments'
                }
              </p>
              <p className="text-sm text-blue-600 mt-1">
                {isParent
                  ? 'Each archer can have their own organization memberships with unique Member IDs.'
                  : 'Your Member ID helps us identify your registrations and past results from each organization.'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Linked Organizations */}
      {filteredOrgs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredOrgs.map((org) => {
            const orgInfo = getOrgInfo(org.org_name);
            return (
              <Card
                key={org.id}
                className="card-base"
                data-testid={`org-card-${org.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${orgInfo.color}`}>
                        <Building2 className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{orgInfo.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={orgInfo.color}>{org.org_name}</Badge>
                          {org.family_archer_name && (
                            <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                              <User className="w-3 h-3 mr-1" />
                              {org.family_archer_name}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(org)}
                        data-testid={`edit-org-${org.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleDelete(org.id)}
                        data-testid={`delete-org-${org.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Member ID:</span>
                      <span className="font-medium text-slate-900">{org.member_id}</span>
                    </div>
                    {org.archer_name && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Registered Name:</span>
                        <span className="font-medium text-slate-900">{org.archer_name}</span>
                      </div>
                    )}
                    {org.bow_type && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Bow Type:</span>
                        <span className="font-medium text-slate-900">{org.bow_type}</span>
                      </div>
                    )}
                    {org.age_class && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Age Class:</span>
                        <span className="font-medium text-slate-900">{org.age_class}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-400">
                      Linked {new Date(org.linked_at).toLocaleDateString()}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="card-base">
          <CardContent className="py-12 text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {filterArcherId !== 'ALL' ? 'No organizations for this filter' : 'No organizations linked'}
            </h3>
            <p className="text-slate-500 mb-4">
              {filterArcherId !== 'ALL' 
                ? 'Try selecting a different archer or "All Organizations"'
                : 'Link your archery organization accounts to start tracking'
              }
            </p>
          </CardContent>
        </Card>
      )}

      {/* Available Organizations */}
      {availableOrgs.length > 0 && linkedOrgs.length > 0 && filterArcherId === 'ALL' && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Available to Link</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {availableOrgs.map((org) => (
              <button
                key={org.id}
                onClick={() => {
                  setFormData({ ...formData, org_name: org.id });
                  setDialogOpen(true);
                }}
                className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-center"
                data-testid={`available-org-${org.id}`}
              >
                <Badge className={org.color}>{org.id}</Badge>
                <p className="text-xs text-slate-500 mt-2 line-clamp-2">{org.name}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
